import { useSupabaseClient } from '~/composables/useSupabaseClient'
import type {
  WorkFileIdentification,
  WorkFileIdentificationCreateParams,
} from '~/entities/workFileIdentification'

export const getWorkFileIdentifications = async (workFileId: number) => {
  const { data, error } = await useSupabaseClient()
    .from('work_file_identifications')
    .select('*')
    .eq('work_file_id', workFileId)
  if (error) throw error
  return data
}

export const createWorkFileIdentifications = async (
  params: WorkFileIdentificationCreateParams[]
) => {
  const { data, error } = await useSupabaseClient()
    .from('work_file_identifications')
    .insert(params)
    .select()
  if (error) throw error
  return data
}

export const updateWorkFileIdentifications = async (
  params: (WorkFileIdentificationCreateParams & { id: number })[]
) => {
  const { data, error } = await useSupabaseClient()
    .from('work_file_identifications')
    .upsert(params, {
      onConflict: 'id',
    })
    .select()
  if (error) throw error
  return data
}

export const getWorkFileIdsByIdentifications = async (
  identifications: {
    id: WorkFileIdentification['check_file_identification_id']
    value: WorkFileIdentification['value']
  }[]
) => {
  identifications = identifications.filter((identification) => identification.value !== '')
  if (identifications.length === 0) return []
  const subquery = useSupabaseClient()
    .from('work_file_identifications')
    .select('id, work_file_id, check_file_identification_id, value')
    .or(
      identifications
        .map(
          (identification) =>
            `and(check_file_identification_id.eq."${escapeFilterString(
              String(identification.id)
            )}",value.ilike."%${escapeFilterString(identification.value)}%")`
        )
        .join(',')
    )
  const { data: identificationData, error: identificationError } = await subquery
  if (identificationError) throw identificationError

  // identifications.length と同じ数だけマッチしている work_file_id を抽出
  const workFileIdCounts = identificationData.reduce((acc, item) => {
    acc[item.work_file_id] = (acc[item.work_file_id] || 0) + 1
    return acc
  }, {} as Record<number, number>)

  const matchedWorkFileIds = Object.entries(workFileIdCounts)
    .filter(([_, count]) => count === identifications.length)
    .map(([workFileId]) => Number(workFileId))

  return matchedWorkFileIds
}

const escapeFilterString = (input: string): string => {
  // ダブルクオートとバックスラッシュが含まれている場合はエスケープする
  return input.replaceAll(/["\\]/g, (match) => `\\${match}`)
}
